import { useId, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { space } from '../../../../styles/onboardingUiTokens';
import { companyConfig } from '../../config/companyConfig';
import { buildPatientSubmitsStepCopy } from '../../insurance/copy';
import { Accordion } from '../elements/Accordion';
import { BottomDrawer } from '../elements/BottomDrawer';
import { Notice } from '../elements/Notice';
import { Body } from '../typography/Body';
import { H3 } from '../typography/HeadingLevels';
import { ListTimeline, ListTimelineItem } from './ListTimeline';
import { useOnboardingChatwoot } from '../../integrations/chatwoot/OnboardingChatwootProvider';

const { support } = companyConfig;

const linkStyle = { color: 'inherit', textDecoration: 'underline' as const };

const FaqSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
  margin-top: ${space.lg};
`;

const SupportNoticeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;

const SupportContactLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xxs};
`;

export type InsuranceInvoiceBillingDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  insuranceShortName: string;
  courseAmount: number;
  refundRate: number;
  /** Insurer reimbursement in EUR (from checkout billing math). */
  reimbursement: number;
  hasBonus: boolean;
  /** True when the insurer makes the patient hand in the course (`isPatientSubmittingInsurer`). */
  patientSubmits: boolean;
};

export function InsuranceInvoiceBillingDetailDrawer({
  open,
  onClose,
  insuranceShortName,
  courseAmount,
  refundRate,
  reimbursement,
  hasBonus,
  patientSubmits,
}: InsuranceInvoiceBillingDetailDrawerProps) {
  const titleId = useId();
  const { openSupportChat, isOpening: isOpeningSupportChat } = useOnboardingChatwoot();
  const billing = useMemo(() => {
    const insurerRefundPercent = Math.round(refundRate * 100);
    const physiofitSubsidyAmount = Math.max(0, courseAmount - reimbursement);
    return { insurerRefundPercent, physiofitSubsidyAmount };
  }, [refundRate, courseAmount, reimbursement]);

  const courseAmountLabel = `${courseAmount}€`;
  const reimbursementLabel = `${reimbursement}€`;
  const isPartialRefund = refundRate < 1;

  const step4Text = isPartialRefund
    ? `Du erhältst von uns und der ${insuranceShortName} die gesamte Kursgebühr zurück. Die ${insuranceShortName} überweist dir ${reimbursementLabel} (${billing.insurerRefundPercent}% der Kursgebühr) und wir dir ${billing.physiofitSubsidyAmount}€ als Förderung.`
    : `Du erhältst von der ${insuranceShortName} die gesamte Kursgebühr zurück.`;

  const faqCostAnswer = isPartialRefund
    ? `Nichts. Die ${insuranceShortName} und Physiofit übernehmen die komplette Kursgebühr für dich.`
    : `Nichts. Die ${insuranceShortName} übernimmt die komplette Kursgebühr für dich.`;

  const submissionStep = patientSubmits
    ? buildPatientSubmitsStepCopy(insuranceShortName)
    : {
        title: `Wir reichen den Kurs bei der ${insuranceShortName} ein`,
        body: `Wir nehmen dir die gesamte Bürokratie ab und reichen den Kurs für dich bei der ${insuranceShortName} ein.`,
      };

  return (
    <BottomDrawer open={open} onClose={onClose} aria-labelledby={titleId} gap={space.md}>
      <H3 id={titleId}>Der Ablauf nach deinem Kursabschluss</H3>
      <Body variant="document" tone="muted">
        Schritt für Schritt zur Erstattung mit deiner Krankenkasse.
      </Body>
      <ListTimeline>
        <ListTimelineItem label="Heute" title="Du startest deinen Kurs" highlighted>
          Du gibst deine Daten für die Abrechnung mit der {insuranceShortName} an und startest den Kurs. Du kannst den
          Kurs jederzeit kostenlos beenden.
        </ListTimelineItem>
        <ListTimelineItem label="Nach 8 Wochen oder später" title="Du schließt den Kurs ab">
          Sobald du den Kurs abgeschlossen hast, erhältst du eine Rechnung über {courseAmountLabel} von uns. Bitte
          überweise diese Rechnung innerhalb von 14 Tagen.
        </ListTimelineItem>
        <ListTimelineItem label="Nach Zahlung" title={submissionStep.title}>
          {submissionStep.body}
        </ListTimelineItem>
        <ListTimelineItem label="Einige Werktage später" title={`Du erhältst ${courseAmountLabel} als Erstattung`}>
          {step4Text}
        </ListTimelineItem>
        {hasBonus ? (
          <ListTimelineItem
            label="Anschließend"
            title={`Hol dir deinen ${insuranceShortName} Bonus`}
            highlighted
            highlightColor={colors.success}
          >
            Du erhältst von uns deine Teilnahmebescheinigung und kannst deinen Bonus beantragen.
          </ListTimelineItem>
        ) : null}
      </ListTimeline>

      <FaqSection aria-labelledby={`${titleId}-faq`}>
        <H3 id={`${titleId}-faq`}>Die häufigsten Fragen</H3>
        <Accordion title="Was kostet mich der Kurs am Ende?">{faqCostAnswer}</Accordion>
        <Accordion title="Wieso muss ich etwas bezahlen wenn es eigentlich kostenlos für mich ist?">
          Das ist leider eine rechtliche Vorgabe. Damit deine Krankenkasse dir das Geld erstatten darf, musst du die
          Gebühr erstmal bezahlen.
        </Accordion>
        <Accordion title="Was passiert wenn mir der Kurs nicht gefällt oder ich aufhören möchte?">
          Überhaupt kein Problem, du kannst den Kurs jederzeit stoppen und aufhören. In diesem Fall kommen keinerlei
          Kosten auf dich zu und du erhältst keine Rechnung von uns.
        </Accordion>
        <Accordion title="Was passiert wenn meine Kasse nicht erstattet?" noBorder>
          Das ist überhaupt kein Problem, in diesem Fall erstatten wir dir deine Zahlung und du bleibst auf keinen
          Kosten sitzen.
        </Accordion>
      </FaqSection>

      <Notice title="Noch eine Frage?" style={{ marginBottom: space.md }}>
        <SupportNoticeContent>
          <SupportContactLines>
            <Body variant="document">
              Hotline Physiofit{' '}
              <a href={support.hotline.tel} style={linkStyle}>
                {support.hotline.display}
              </a>
            </Body>
            <Body variant="document">
              Mail{' '}
              <a href={`mailto:${support.email}`} style={linkStyle}>
                {support.email}
              </a>
            </Body>
            <Body variant="document">
              Chatte mit uns:{' '}
              <a
                href="#!"
                style={{
                  ...linkStyle,
                  opacity: isOpeningSupportChat ? 0.6 : 1,
                  pointerEvents: isOpeningSupportChat ? 'none' : undefined,
                }}
                aria-busy={isOpeningSupportChat || undefined}
                onClick={(event) => {
                  event.preventDefault();
                  if (isOpeningSupportChat) return;
                  void openSupportChat();
                }}
              >
                Chat öffnen
              </a>
            </Body>
          </SupportContactLines>
        </SupportNoticeContent>
      </Notice>
    </BottomDrawer>
  );
}
